// Note: Install @reduxjs/toolkit and react-redux 
// npm install @reduxjs/toolkit react-redux

import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { CVUploadState, ExtractedData, ManualFormData } from '../../../components/cv-upload/types';

const initialManualData: ManualFormData = {
  name: "",
  birthYear: 0,
  email: "",
  phone: "",
  position: "",
  level: "",
  yearsOfExperience: 0,
  managementExperience: 0,
  languages: "",
  languageLevels: "",
  preferredLocation: "",
  skills: "",
};

const initialState: CVUploadState = {
  isPopupOpen: false,
  file: null,
  previewUrl: null,
  activeTab: "upload",
  dragActive: false,
  isManualEntry: false,
  manualData: initialManualData,
  extractedData: null,
  uploadStatus: {
    isUploading: false,
    uploadProgress: 0,
    isProcessing: false,
  },
};

export const cvUploadSlice = createSlice({
  name: 'cvUpload',
  initialState,
  reducers: {
    setFile: (state, action: PayloadAction<File | null>) => {
      state.file = action.payload;
      
      // Create preview URL if it's a PDF
      if (action.payload && action.payload.type === "application/pdf") {
        // Note: In a real Redux implementation, we'd handle this side effect differently
        // This is a placeholder to show the logic
        state.previewUrl = "PDF_URL_HERE"; // URL.createObjectURL() is a side effect
      } else {
        state.previewUrl = null;
      }
    },
    
    setActiveTab: (state, action: PayloadAction<string>) => {
      state.activeTab = action.payload;
    },
    
    skipToProfile: (state) => {
      state.activeTab = "profile";
    },
    
    setDragActive: (state, action: PayloadAction<boolean>) => {
      state.dragActive = action.payload;
    },
    
    setIsManualEntry: (state, action: PayloadAction<boolean>) => {
      state.isManualEntry = action.payload;
    },
    
    updateManualData: (state, action: PayloadAction<Partial<ManualFormData>>) => {
      state.manualData = { ...state.manualData, ...action.payload };
    },
    
    setExtractedData: (state, action: PayloadAction<ExtractedData | null>) => {
      state.extractedData = action.payload;
    },
    
    startUpload: (state) => {
      state.uploadStatus.isUploading = true;
      state.uploadStatus.uploadProgress = 0;
    },
    
    updateUploadProgress: (state, action: PayloadAction<number>) => {
      state.uploadStatus.uploadProgress = action.payload;
    },
    
    finishUpload: (state) => {
      state.uploadStatus.isUploading = false;
      state.uploadStatus.uploadProgress = 100;
    },
    
    startProcessing: (state) => {
      state.uploadStatus.isProcessing = true;
    },
    
    finishProcessing: (state) => {
      state.uploadStatus.isProcessing = false;
    },
    
    resetForm: (state) => {
      state.file = null;
      state.previewUrl = null;
      state.extractedData = null;
      state.activeTab = "upload";
      state.isPopupOpen = false;
    },
    
    openPopup: (state) => {
      state.isPopupOpen = true;
    },
    
    closePopup: (state) => {
      state.isPopupOpen = false;
    },
  },
});

export const {
  setFile,
  setActiveTab,
  skipToProfile,
  setDragActive,
  setIsManualEntry,
  updateManualData,
  setExtractedData,
  startUpload,
  updateUploadProgress,
  finishUpload,
  startProcessing,
  finishProcessing,
  resetForm,
  openPopup,
  closePopup,
} = cvUploadSlice.actions;

export default cvUploadSlice.reducer; 